// Lightweight gettext-style i18n. English strings are the keys; Latvian is an
// override map. Missing translations fall back to the English source string.
export type Lang = "en" | "lv";

const LV: Record<string, string> = {
  // Join / landing
  "Quizzly": "Quizzly",
  "Enter the code on the big screen to jump in.": "Ievadi kodu no lielā ekrāna, lai pievienotos.",
  "Game code": "Spēles kods",
  "Nickname": "Segvārds",
  "Your name": "Tavs vārds",
  "Let's play! 🎉": "Spēlēsim! 🎉",
  "Enter the game code": "Ievadi spēles kodu",
  "Enter your name": "Ievadi savu vārdu",
  "Running the quiz? <b>Host area →</b>": "Vadi viktorīnu? <b>Vadītāja zona →</b>",

  // Studio auth
  "Quizzly Studio": "Quizzly studija",
  "Host area — quizmasters only.": "Vadītāja zona — tikai viktorīnu vadītājiem.",
  "Password": "Parole",
  "Enter studio ✨": "Ieiet studijā ✨",
  "Wrong password": "Nepareiza parole",
  "Log out": "Iziet",

  // Dashboard
  "Your quizzes": "Tavas viktorīnas",
  "＋ New quiz": "＋ Jauna viktorīna",
  "No quizzes yet — make your first one! 🎈": "Vēl nav neviena viktorīna — izveido savu pirmo! 🎈",
  "No description": "Nav apraksta",
  "rounds": "kārtas",
  "questions": "jautājumi",
  "Delete": "Dzēst",

  // Editor
  "← Quizzes": "← Viktorīnas",
  "▶ Host game": "▶ Vadīt spēli",
  "Saved ✓": "Saglabāts ✓",
  "Save changes": "Saglabāt izmaiņas",
  "Saving…": "Saglabā…",
  "Save failed ⚠": "Neizdevās saglabāt ⚠",
  "Quiz title": "Viktorīnas nosaukums",
  "Description": "Apraksts",
  "Description (optional)": "Apraksts (nav obligāts)",
  "Rounds": "Kārtas",
  "＋ Add round": "＋ Pievienot kārtu",
  "＋ Add question": "＋ Pievienot jautājumu",
  "Multiple choice": "Atbilžu varianti",
  "Type answer": "Ievadīt atbildi",
  "Question prompt": "Jautājums",
  "Media:": "Medijs:",
  "🖼 Image": "🖼 Attēls",
  "🎵 Audio (mp3)": "🎵 Audio (mp3)",
  "▶️ YouTube": "▶️ YouTube",
  "✂️ Trim clip": "✂️ Apgriezt fragmentu",
  "Remove": "Noņemt",
  "Uploading…": "Augšupielādē…",
  "Mark correct": "Atzīmēt pareizo",
  "✓ correct": "✓ pareizā",
  "mark correct": "atzīmēt pareizo",
  "＋ Add answer": "＋ Pievienot atbildi",
  "Accepted answers (case/punctuation ignored). Add a few spellings.":
    "Pieņemtās atbildes (reģistrs un pieturzīmes netiek ņemtas vērā). Pievieno dažādus rakstības variantus.",
  "Accepted answer": "Pieņemtā atbilde",
  "＋ Add accepted answer": "＋ Pievienot pieņemto atbildi",
  "Time (s)": "Laiks (s)",
  "Base points": "Bāzes punkti",
  "Reveal note": "Piezīme (rāda pēc atbildes)",
  "Reveal note (shown after answer)": "Piezīme (rāda pēc atbildes)",
  "Award a bonus to the first correct answer": "Piešķirt bonusu pirmajai pareizajai atbildei",
  "Discard unsaved changes?": "Atmest nesaglabātās izmaiņas?",

  // Trim modal
  "✂️ Trim the clip": "✂️ Apgriezt fragmentu",
  "Drag the pink handles to pick the segment players will hear.":
    "Velc rozā rokturus, lai izvēlētos fragmentu, ko dzirdēs spēlētāji.",
  "▶ Preview": "▶ Priekšskatīt",
  "Cancel": "Atcelt",
  "Save clip": "Saglabāt fragmentu",
  "Clip": "Fragments",

  // Player game
  "You're in!": "Tu esi iekšā!",
  "Look up at the big screen — the quiz is about to start.":
    "Skaties uz lielo ekrānu — viktorīna tūlīt sāksies.",
  "Locked in!": "Atbilde reģistrēta!",
  "Waiting for everyone else…": "Gaidām pārējos…",
  "Fingers crossed 🤞": "Turam īkšķus 🤞",
  "Correct!": "Pareizi!",
  "Not this time": "Šoreiz ne",
  "Total:": "Kopā:",
  "Time's up!": "Laiks beidzies!",
  "Just missed it — get the next one 💪": "Tikko nepaspēji — pieķer nākamo 💪",
  "Round standings": "Kārtas rezultāti",
  "Leaderboard": "Rezultātu tabula",
  "Eyes on the big screen 👀": "Skaties uz lielo ekrānu 👀",
  "You won! 🏆": "Tu uzvarēji! 🏆",
  "Thanks for playing 💗": "Paldies, ka spēlēji 💗",
  "Submit answer": "Iesniegt atbildi",
  "Type your answer…": "Raksti savu atbildi…",
  "🎵 Name that tune!": "🎵 Nosauc dziesmu!",
  "That code isn't live — check the screen.": "Šis kods nav aktīvs — pārbaudi ekrānu.",

  // Host / big screen
  "Join at": "Pievienojies šeit",
  "Start quiz ▶": "Sākt viktorīnu ▶",
  "Connecting…": "Savienojas…",
  "Waiting for players…": "Gaidām spēlētājus…",
  "Start timer ⏱": "Sākt laiku ⏱",
  "Reveal answer 👀": "Parādīt atbildi 👀",
  "Reveal now": "Parādīt tagad",
  "Name that tune!": "Nosauc dziesmu!",
  "🔊 Play clip": "🔊 Atskaņot fragmentu",
  "⌨️ Players are typing their answers…": "⌨️ Spēlētāji raksta savas atbildes…",
  "Answer!": "Atbilde!",
  "Accepted answers": "Pieņemtās atbildes",
  "Tap to override marking:": "Pieskaries, lai mainītu vērtējumu:",
  "Leaderboard 🏆": "Rezultātu tabula 🏆",
  "Round standings 🎵": "Kārtas rezultāti 🎵",
  "Skip to next →": "Pāriet uz nākamo →",
  "Next question →": "Nākamais jautājums →",
  "New game": "Jauna spēle",
  "Game over": "Spēle beigusies",
  "Host session expired": "Vadītāja sesija beigusies",
  "Start the game again from the studio.": "Sāc spēli no jauna studijā.",
  "Go to studio": "Uz studiju",

  // Composed-phrase fragments introduced while wrapping the UI
  "you": "tu",
  "Option": "Variants",
  "Option ": "Variants ",
  " pts": " p.",
  "You finished #": "Tu ieņēmi vietu #",
  "answered": "atbildējuši",
  "player in the lobby": "spēlētājs gaida",
  "players in the lobby": "spēlētāji gaida",
  "Round": "Kārta",
  "Question": "Jautājums",
  "round": "kārta",
  "question": "jautājums",
  "wins!": "uzvar!",
  "Answer": "Atbilde",
  "Untitled quiz": "Nenosaukta viktorīna",
  // Studio dialog/prompt strings
  "Move up": "Pārvietot augšup",
  "Move down": "Pārvietot lejup",
  "Delete round": "Dzēst kārtu",
  "Delete this round and its questions?": "Dzēst šo kārtu un tās jautājumus?",
  "Paste a YouTube link — we'll grab the audio:": "Ielīmē YouTube saiti — mēs paņemsim audio:",
  "⏬ Downloading audio from YouTube… (this can take ~20s)": "⏬ Lejupielādē audio no YouTube… (var aizņemt ~20s)",
  "Download failed — try another link": "Neizdevās lejupielādēt — pamēģini citu saiti",
  "Save failed: ": "Neizdevās saglabāt: ",
  "Upload failed: ": "Neizdevās augšupielādēt: ",
  "Couldn't start game: ": "Neizdevās sākt spēli: ",
  // Co-host, import/export, staged reveal
  "👥 Co-host": "👥 Līdzvadīt",
  "Enter the game code shown on the host screen:": "Ievadi spēles kodu, kas redzams uz vadītāja ekrāna:",
  "⬆ Import": "⬆ Importēt",
  "Import failed: ": "Importēšana neizdevās: ",
  "Export": "Eksportēt",
  "Get ready…": "Sagatavojies…",
  "Round title in Latvian (optional)": "Kārtas nosaukums latviski (nav obligāts)",
  "Question in Latvian (optional)": "Jautājums latviski (nav obligāts)",
  // Scoreboard + simulation
  "Round scoreboard 🏆": "Kārtas rezultāti 🏆",
  "Round scoreboard": "Kārtas rezultāti",
  "Player": "Spēlētājs",
  "This round": "Šajā kārtā",
  "Total": "Kopā",
  "🧪 Simulate": "🧪 Simulācija",
  "How many practice players? (they answer automatically)": "Cik treniņa spēlētāju? (viņi atbild automātiski)",
  "Couldn't start simulation: ": "Neizdevās sākt simulāciju: ",
};

const DICTS: Record<Lang, Record<string, string>> = { en: {}, lv: LV };
const KEY = "quizzly_lang";

let current: Lang = "en";

export function getLang(): Lang {
  return current;
}

export function setLang(l: Lang) {
  current = l;
  try {
    localStorage.setItem(KEY, l);
  } catch {}
}

// Translate a source (English) string for the current language.
export function t(s: string): string {
  return DICTS[current][s] ?? s;
}

// Pick author-entered content (round titles, question prompts) for the current
// language, falling back to the English/original text when no LV variant exists.
export function pickLang(en: string, lv?: string | null): string {
  return current === "lv" && lv && lv.trim() ? lv : en;
}

// Format a count phrase, e.g. plural(n, "player", "players"). Latvian uses the
// singular for counts ending in 1 (except 11): "1 kārta", "21 kārta", "11 kārtas".
export function plural(n: number, one: string, many: string): string {
  const useSingular = current === "lv" ? n % 10 === 1 && n % 100 !== 11 : n === 1;
  return `${n} ${t(useSingular ? one : many)}`;
}

// Resolve initial language WITHOUT blocking render:
//   saved pref > browser language (instant) > geo LV check (background).
// If the visitor has no saved choice and turns out to be on a Latvian IP, we
// switch to Latvian and reload once. English visitors never wait on geo.
export function initLang(): Lang {
  let saved: Lang | null = null;
  try {
    saved = localStorage.getItem(KEY) as Lang | null;
  } catch {}
  if (saved === "en" || saved === "lv") {
    current = saved;
    return current;
  }
  current = navigator.language?.toLowerCase().startsWith("lv") ? "lv" : "en";
  // Background geo check for the "Latvian IP, non-Latvian browser" case.
  fetch("/api/geo")
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { country?: string } | null) => {
      if (d?.country === "LV" && current !== "lv") {
        setLang("lv");
        location.reload();
      }
    })
    .catch(() => {});
  return current;
}
